// Library-folder integration via the File System Access API (Chrome/Edge).
// Lets the user point ScrollWind at a real folder on disk: existing EPUB/PDF
// files there get linked to library books, and downloads are written into it.

import { saveFolderHandle, loadFolderHandle, clearFolderHandle } from './db';

export function isFolderSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

/** Normalise a title or filename for fuzzy matching. */
export function normalizeName(s: string): string {
  return s
    .toLowerCase()
    .replace(/\.(epub|pdf)$/i, '')
    .replace(/[^a-z0-9]+/g, '');
}

export function extFor(type: 'PDF' | 'EPUB'): string {
  return type === 'PDF' ? 'pdf' : 'epub';
}

/** Filesystem-safe filename for a saved download. */
export function fileNameFor(title: string, type: 'PDF' | 'EPUB'): string {
  const base = title.replace(/[<>:"/\\|?* -]+/g, '_').replace(/\.+$/, '').trim() || 'book';
  return `${base}.${extFor(type)}`;
}

export async function pickFolder(): Promise<FileSystemDirectoryHandle | null> {
  if (!isFolderSupported()) return null;
  const handle = await window.showDirectoryPicker({ id: 'scrollwind-library', mode: 'readwrite' });
  await saveFolderHandle(handle);
  return handle;
}

export async function getFolder(): Promise<FileSystemDirectoryHandle | undefined> {
  return loadFolderHandle();
}

export async function forgetFolder(): Promise<void> {
  await clearFolderHandle();
}

/** Whether we currently hold permission (does not prompt). */
export async function hasPermission(handle: FileSystemDirectoryHandle, write = false): Promise<boolean> {
  if (!handle.queryPermission) return true;
  const state = await handle.queryPermission({ mode: write ? 'readwrite' : 'read' });
  return state === 'granted';
}

/** Request permission — must be called from a user gesture. */
export async function ensurePermission(handle: FileSystemDirectoryHandle, write = false): Promise<boolean> {
  const mode = write ? 'readwrite' : 'read';
  if (handle.queryPermission && (await handle.queryPermission({ mode })) === 'granted') return true;
  if (!handle.requestPermission) return true;
  return (await handle.requestPermission({ mode })) === 'granted';
}

export interface FolderFile {
  name: string;
  norm: string;
}

/** List the EPUB/PDF files in the folder (top level). */
export async function scanFolder(handle: FileSystemDirectoryHandle): Promise<FolderFile[]> {
  const files: FolderFile[] = [];
  for await (const entry of handle.values()) {
    if (entry.kind === 'file' && /\.(epub|pdf)$/i.test(entry.name)) {
      files.push({ name: entry.name, norm: normalizeName(entry.name) });
    }
  }
  return files;
}

export async function readFolderFile(handle: FileSystemDirectoryHandle, name: string): Promise<Blob | null> {
  try {
    const fh = await handle.getFileHandle(name);
    return await fh.getFile();
  } catch {
    return null;
  }
}

export async function writeFolderFile(
  handle: FileSystemDirectoryHandle,
  name: string,
  blob: Blob,
): Promise<void> {
  const fh = await handle.getFileHandle(name, { create: true });
  const writable = await fh.createWritable();
  await writable.write(blob);
  await writable.close();
}
